/**
 * Mock Data — Resource Scheduling Integration
 *
 * Provides all mock data required for frontend development of the Resource
 * Scheduling feature (PRD: docs/features/resource-scheduling/
 * PRD_RESOURCE_SCHEDULING.md).
 *
 * No live scheduler connection is required while using this data. All screens
 * that need scheduling data should import from this file during development.
 *
 * When the real backend API is ready, replace these imports with real API calls
 * using the endpoint contracts defined in PRD Section 9.
 */

// ---------------------------------------------------------------------------
// Feature Flag
// ---------------------------------------------------------------------------

/**
 * Controls global visibility of all scheduling UI surfaces:
 *   - Scheduling tab on Project Details
 *   - Dashboard Resourcing Alerts panel
 *   - Dashboard project table Booking Health columns
 *
 * Maps to: GET /api/features → { schedulerConnected: boolean }
 */
export const mockSchedulerFeatureFlag = {
  schedulerConnected: true,
} as const;

// ---------------------------------------------------------------------------
// Scheduler Connection
// ---------------------------------------------------------------------------

export type SchedulerType = 'AccessPeoplePlanner' | 'AccessPlanner';

export type SchedulerConnectionStatus = 'connected' | 'disconnected' | 'not_configured' | 'error';

export interface SchedulerConnection {
  /** Which scheduling product is connected */
  type: SchedulerType;
  /** Human-readable display name for the connected scheduler */
  displayName: string;
  /** Current connection health */
  status: SchedulerConnectionStatus;
  /** ISO timestamp of the last successful data sync */
  lastSynced: string | null;
  /** Present when status === 'error' */
  errorMessage?: string;
  /** Base URL of the connected scheduler instance for deep linking */
  schedulerUrl?: string;
}

/**
 * Mock connected-state for Settings → Integrations → Resource Scheduling card.
 *
 * Swap `status` to 'disconnected' or 'error' to test the other card states.
 *
 * Maps to: GET /api/settings/integrations/scheduler
 */
export const mockSchedulerConnection: SchedulerConnection = {
  type: 'AccessPeoplePlanner',
  displayName: 'Access Planner',
  status: 'connected',
  lastSynced: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // ~2 hours ago
  schedulerUrl: 'https://planner.accessacloud.com',
};

// ---------------------------------------------------------------------------
// Bookings
// ---------------------------------------------------------------------------

export type BookingStatus = 'Planned' | 'Confirmed' | 'Cancelled' | 'Completed';
export type BookingSource = 'Scheduler' | 'Projects';

export interface SchedulingBooking {
  bookingId: string;
  personId: string | null;
  personName: string | null;
  /** URL to the person's avatar image; null for unallocated Planned bookings */
  personAvatar: string | null;
  roleId: string;
  roleName: string;
  startDate: string; // ISO date string YYYY-MM-DD
  endDate: string;   // ISO date string YYYY-MM-DD
  totalHours: number;
  /** Actual hours timesheeted against this booking period */
  actualHours: number | null;
  /** Booking status as normalised by the Projects API adapter layer */
  status: BookingStatus;
  /** How this booking was created */
  source: BookingSource;
}

/**
 * Three bookings for the mock "Alpha Replatform" project (PRD Section 8):
 *   b001 — Sarah Jones, Senior Consultant, Feb 1–14, 60 hrs, Confirmed
 *   b002 — John Smith,  Senior Consultant, Feb 17–28, 60 hrs, Confirmed
 *   b003 — Unassigned,  Junior Consultant, Feb 1–28,  30 hrs, Planned (raised from Projects)
 *
 * Maps to: GET /api/projects/{id}/scheduling/bookings
 */
export const mockSchedulingBookings: SchedulingBooking[] = [
  {
    bookingId: 'b001',
    personId: 'p001',
    personName: 'Ben Carter',
    personAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
    roleId: 'r001',
    roleName: 'Project Manager',
    startDate: '2025-02-01',
    endDate: '2025-02-14',
    totalHours: 60,
    actualHours: 52,
    status: 'Confirmed',
    source: 'Scheduler',
  },
  {
    bookingId: 'b002',
    personId: 'p002',
    personName: 'Catherine Davies',
    personAvatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400',
    roleId: 'r002',
    roleName: 'Senior Consultant',
    startDate: '2025-02-17',
    endDate: '2025-02-28',
    totalHours: 60,
    actualHours: 60,
    status: 'Confirmed',
    source: 'Scheduler',
  },
  {
    bookingId: 'b003',
    personId: null,
    personName: null,
    personAvatar: null,
    roleId: 'r002',
    roleName: 'Junior Consultant',
    startDate: '2025-02-01',
    endDate: '2025-02-28',
    totalHours: 30,
    actualHours: null, // No timesheet hours yet — booking is Planned/unassigned
    status: 'Planned',
    source: 'Projects',
  },
];

// ---------------------------------------------------------------------------
// Role Coverage
// ---------------------------------------------------------------------------

export type RoleCoverageStatus =
  | 'FullyCovered'
  | 'PartiallyCovered'
  | 'NotCovered'
  | 'OverScheduled';

export interface RoleCoveragePerson {
  personId: string;
  personName: string;
  scheduledHours: number;
  actualHours: number;
}

export interface RoleCoverage {
  roleId: string;
  roleName: string;
  /** Hours budgeted for this role in the Resource Budget */
  budgetHours: number;
  /** Total hours booked in the scheduler for this role */
  scheduledHours: number;
  /** Hours timesheeted against this project for people in this role */
  actualHours: number;
  /** budgetHours − actualHours */
  remainingHours: number;
  /** scheduledHours / budgetHours × 100 (0–100+) */
  coveragePercent: number;
  status: RoleCoverageStatus;
  /** Individual people booked against this role (expandable rows) */
  people: RoleCoveragePerson[];
  /**
   * True when a Planned (unallocated) booking already exists in the scheduler
   * for this role — disables the "Raise Request" button.
   */
  hasPendingRequest?: boolean;
}

/**
 * Role-level budget vs scheduled reconciliation for "Alpha Replatform".
 *
 * r001 Senior Consultant  — 120 / 200 hrs → 60%  Partially Covered
 * r002 Junior Consultant  —  30 / 150 hrs → 20%  Not Covered
 * r003 Project Manager    — 100 / 100 hrs → 100% Fully Covered
 *
 * Maps to: GET /api/projects/{id}/scheduling/role-coverage
 */
export const mockRoleCoverage: RoleCoverage[] = [
  {
    roleId: 'r001',
    roleName: 'Senior Consultant',
    budgetHours: 200,
    scheduledHours: 120,
    actualHours: 80,
    remainingHours: 120, // 200 − 80
    coveragePercent: 60,
    status: 'PartiallyCovered',
    people: [
      { personId: 'p001', personName: 'Ben Carter',       scheduledHours: 60, actualHours: 52 },
      { personId: 'p002', personName: 'Catherine Davies', scheduledHours: 60, actualHours: 60 },
    ],
  },
  {
    roleId: 'r002',
    roleName: 'Junior Consultant',
    budgetHours: 150,
    scheduledHours: 30,
    actualHours: 15,
    remainingHours: 135, // 150 − 15
    coveragePercent: 20,
    status: 'NotCovered',
    hasPendingRequest: true, // Demonstrates the disabled "Raise Request" button state
    people: [],
  },
  {
    roleId: 'r003',
    roleName: 'Project Manager',
    budgetHours: 100,
    scheduledHours: 100,
    actualHours: 60,
    remainingHours: 40, // 100 − 60
    coveragePercent: 100,
    status: 'FullyCovered',
    people: [
      { personId: 'p003', personName: 'Claire Hughes', scheduledHours: 100, actualHours: 60 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Warnings
// ---------------------------------------------------------------------------

export type WarningSeverity = 'critical' | 'warning' | 'info';
export type WarningType =
  | 'NoConfirmedBookings'
  | 'LowCoverage'
  | 'BookingsExtendBeyondEnd'
  | 'PartialCoverage'
  | 'UnbookedTimesheeters'
  | 'PendingResourceRequest';

export interface SchedulingWarning {
  type: WarningType;
  severity: WarningSeverity;
  message: string;
}

/**
 * Active warnings for "Alpha Replatform".
 *
 * Maps to: GET /api/projects/{id}/scheduling/warnings
 */
export const mockSchedulingWarnings: SchedulingWarning[] = [
  {
    type: 'PartialCoverage',
    severity: 'warning',
    message:
      '80% of required hours are scheduled. 220 hrs of demand is unmet across 2 roles.',
  },
  {
    type: 'UnbookedTimesheeters',
    severity: 'warning',
    message:
      '1 person has logged time on this project without a scheduler booking.',
  },
];

// ---------------------------------------------------------------------------
// Scheduling Summary (Overview Tab Widget)
// ---------------------------------------------------------------------------

export type SchedulingHealthStatus =
  | 'FullyResourced'
  | 'PartiallyResourced'
  | 'Unresourced'
  | 'NoData';

export interface UnmetRole {
  roleId: string;
  roleName: string;
  budgetHours: number;
  scheduledHours: number;
  gapHours: number;
}

export interface SchedulingSummary {
  totalBudgetHours: number;
  totalScheduledHours: number;
  coveragePercent: number;
  totalBookings: number;
  confirmedBookings: number;
  plannedBookings: number;
  /** Roles where scheduled hours < budget hours */
  unmetRoles: UnmetRole[];
  /** ISO date string — latest booking end date */
  bookingsEndDate: string;
  /** ISO date string — project end date */
  projectEndDate: string;
  healthStatus: SchedulingHealthStatus;
  warnings: SchedulingWarning[];
}

/**
 * Summary data for the Overview tab Scheduling widget on "Alpha Replatform".
 *
 * 480 / 600 total budget hrs → 80% coverage → Partially Resourced
 * Note: totalBudgetHours (600) = sum of all role budgets from mockRoleCoverage (200+150+100+150 spare).
 * totalScheduledHours (480) — bookings from scheduler mapped to budget roles.
 *
 * Maps to: GET /api/projects/{id}/scheduling/summary
 */
export const mockSchedulingSummary: SchedulingSummary = {
  totalBudgetHours: 600,
  totalScheduledHours: 480,
  coveragePercent: 80,
  totalBookings: 6,
  confirmedBookings: 4,
  plannedBookings: 2,
  unmetRoles: [
    {
      roleId: 'r001',
      roleName: 'Senior Consultant',
      budgetHours: 200,
      scheduledHours: 120,
      gapHours: 80,
    },
    {
      roleId: 'r002',
      roleName: 'Junior Consultant',
      budgetHours: 150,
      scheduledHours: 30,
      gapHours: 120,
    },
  ],
  bookingsEndDate: '2025-03-15',
  projectEndDate: '2025-03-29',
  healthStatus: 'PartiallyResourced',
  warnings: mockSchedulingWarnings,
};

// ---------------------------------------------------------------------------
// Dashboard — Resourcing Alerts
// ---------------------------------------------------------------------------

export type AlertSeverity = 'critical' | 'warning';

export interface DashboardSchedulingAlert {
  projectId: string;
  projectName: string;
  pmName: string;
  healthStatus: SchedulingHealthStatus;
  /** Short, plain-English description of the primary issue */
  primaryAlertMessage: string;
  severity: AlertSeverity;
  /** ISO date string — used for sorting (soonest first within severity) */
  projectStartDate: string;
}

/**
 * Portfolio-level resourcing alerts for the Dashboard panel (PRD Section 7.7).
 * Ordered: Critical first → then Warning; within each group, soonest start date first.
 *
 * Maps to: GET /api/dashboard/scheduling-alerts (frontend shows top 5)
 */
export const mockDashboardAlerts: DashboardSchedulingAlert[] = [
  // ── Critical ────────────────────────────────────────────────────────────
  {
    projectId: 'proj-101',
    projectName: 'Alpha Replatform',
    pmName: 'Jane Davies',
    healthStatus: 'Unresourced',
    primaryAlertMessage: 'Starts in 7 days · 0% booked',
    severity: 'critical',
    projectStartDate: '2025-02-07',
  },
  {
    projectId: 'proj-102',
    projectName: 'Beta Migration',
    pmName: 'Steve Morris',
    healthStatus: 'Unresourced',
    primaryAlertMessage: '40% booked · 3 roles unmet',
    severity: 'critical',
    projectStartDate: '2025-02-14',
  },
  // ── Warning ─────────────────────────────────────────────────────────────
  {
    projectId: 'proj-103',
    projectName: 'Gamma Integration',
    pmName: 'Claire Hughes',
    healthStatus: 'PartiallyResourced',
    primaryAlertMessage: 'Bookings end before project end',
    severity: 'warning',
    projectStartDate: '2025-02-21',
  },
  {
    projectId: 'proj-104',
    projectName: 'Delta Rollout',
    pmName: 'Mark Thomas',
    healthStatus: 'PartiallyResourced',
    primaryAlertMessage: '80% booked · 2 unallocated',
    severity: 'warning',
    projectStartDate: '2025-03-01',
  },
  {
    projectId: 'proj-105',
    projectName: 'Epsilon Phase 2',
    pmName: 'Jane Davies',
    healthStatus: 'PartiallyResourced',
    primaryAlertMessage: 'Resource request pending',
    severity: 'warning',
    projectStartDate: '2025-03-10',
  },
];

// ---------------------------------------------------------------------------
// Dashboard — Project Table Booking Health (per-project column data)
// ---------------------------------------------------------------------------

export interface ProjectBookingHealth {
  projectId: string;
  /** 0–100; null if no scheduler data exists for this project */
  coveragePercent: number | null;
  /** Short plain-English summary of the most critical issue, or "Fully Resourced" */
  statusSummary: string;
  healthStatus: SchedulingHealthStatus | null;
}

/**
 * Per-project booking health data for the Dashboard project table
 * (PRD Section 7.8 — two new columns: Booking Coverage + Booking Status).
 *
 * Maps to: GET /api/projects — extended with `schedulingHealth` per project.
 */
export const mockProjectBookingHealth: ProjectBookingHealth[] = [
  {
    projectId: 'proj-101',
    coveragePercent: 0,
    statusSummary: 'No bookings',
    healthStatus: 'Unresourced',
  },
  {
    projectId: 'proj-102',
    coveragePercent: 40,
    statusSummary: '3 roles unmet',
    healthStatus: 'Unresourced',
  },
  {
    projectId: 'proj-103',
    coveragePercent: 80,
    statusSummary: 'Ends early',
    healthStatus: 'PartiallyResourced',
  },
  {
    projectId: 'proj-104',
    coveragePercent: 100,
    statusSummary: 'Fully resourced',
    healthStatus: 'FullyResourced',
  },
  {
    projectId: 'proj-105',
    coveragePercent: null,
    statusSummary: '—',
    healthStatus: null,
  },
];

// ---------------------------------------------------------------------------
// Team Tab — Booking Status per person
// ---------------------------------------------------------------------------

export type PersonBookingStatus = 'Confirmed' | 'Planned' | 'NoBooking';

export interface TeamMemberBookingStatus {
  personId: string;
  personName: string;
  role: string;
  allocatedHours: number;
  actualHours: number;
  bookingStatus: PersonBookingStatus;
}

/**
 * Extends the existing Team tab data with a "Booking Status" column for each person.
 * Used to render the ⚠ unbooked timesheeters warning banner and the per-row status pill.
 *
 * Maps to: GET /api/projects/{id}/team — extended with `bookingStatus` per member.
 */
export const mockTeamBookingStatuses: TeamMemberBookingStatus[] = [
  {
    personId: 'p001',
    personName: 'Ben Carter',
    role: 'Project Manager',
    allocatedHours: 80,
    actualHours: 60,
    bookingStatus: 'Confirmed',
  },
  {
    personId: 'p002',
    personName: 'Catherine Davies',
    role: 'Senior Consultant',
    allocatedHours: 120,
    actualHours: 110,
    bookingStatus: 'Confirmed',
  },
  {
    personId: 'p003',
    personName: 'Noah Martin',
    role: 'Solutions Architect',
    allocatedHours: 0,
    actualHours: 22,
    bookingStatus: 'NoBooking',
  },
];

/**
 * Derived count — how many team members have logged time but have no booking.
 * Used to drive the warning banner copy in the Team tab.
 */
export const mockUnbookedTimesheetersCount = mockTeamBookingStatuses.filter(
  (m) => m.bookingStatus === 'NoBooking',
).length;

/**
 * Names of people who have logged time but have no scheduler booking.
 * Used to pre-filter the Time tab when the user clicks "View in Time" from the banner.
 */
export const mockUnbookedTimesheetersNames = mockTeamBookingStatuses
  .filter((m) => m.bookingStatus === 'NoBooking')
  .map((m) => m.personName);

// ---------------------------------------------------------------------------
// Resource Requests
// ---------------------------------------------------------------------------

export type ResourceRequestStatus =
  | 'Pending'
  | 'InProgress'
  | 'Filled'
  | 'Cancelled';

export type ResourceRequestPriority = 'nice-to-have' | 'important' | 'critical';

export interface ResourceRequest {
  requestId: string;
  roleName: string;
  hoursRequired: number;
  startDate: string;
  endDate: string;
  priority: ResourceRequestPriority;
  notes?: string;
  status: ResourceRequestStatus;
  /** ISO timestamp of when the request was raised */
  raisedAt: string;
  /** Name of the person assigned to fill this request, if status is Filled */
  assignedTo?: string;
}

/**
 * Mock resource requests raised against "Alpha Replatform".
 *
 * Maps to: GET /api/projects/{id}/scheduling/requests
 */
export const mockResourceRequests: ResourceRequest[] = [
  {
    requestId: 'req-001',
    roleName: 'Junior Consultant',
    hoursRequired: 120,
    startDate: '2025-03-01',
    endDate: '2025-04-30',
    priority: 'important',
    notes: 'Need someone to support the data migration workstream.',
    status: 'InProgress',
    raisedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
  },
  {
    requestId: 'req-002',
    roleName: 'Senior Consultant',
    hoursRequired: 80,
    startDate: '2025-02-17',
    endDate: '2025-03-14',
    priority: 'critical',
    status: 'Filled',
    raisedAt: new Date(Date.now() - 10 * 24 * 60 * 60 * 1000).toISOString(), // 10 days ago
    assignedTo: 'John Smith',
  },
  {
    requestId: 'req-003',
    roleName: 'Project Manager',
    hoursRequired: 40,
    startDate: '2025-04-01',
    endDate: '2025-04-30',
    priority: 'nice-to-have',
    notes: 'Part-time PM support for UAT phase.',
    status: 'Pending',
    raisedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
  },
];
